// V4.23 — dynamic result-set groups: ONE dimension (body_type, model,
// fuel, price_band, city, …) chosen by the AI, groups built server-side
// from the SAME cascade result set. Expandable, fully interactive.
// Labels ALWAYS pass through the central enum/i18n layer — raw
// taxonomy slugs never reach the UI (V4.23 Polish).
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Layers, MapPin } from "lucide-react";
import { enumLabel } from "../../../utils/enumLabels";

const NUM_LOCALES = { de: "de-AT", hr: "hr-HR", en: "en-GB" };

function fmtNum(v, lang) {
  return Number(v).toLocaleString(NUM_LOCALES[lang] || "de-AT");
}

function fmtEur(v, lang = "de") {
  if (v == null || Number.isNaN(v)) return "—";
  return `€ ${fmtNum(Math.round(v), lang)}`;
}

// Language-neutral meta from the backend → label in the ACTIVE UI
// language (enum → central layer, ranges → locale number format).
function groupLabel(g, groupBy, t, lang) {
  const m = g.meta || {};
  if (m.kind === "enum") return enumLabel(groupBy, g.key);
  if (m.kind === "rooms") return t("widgets.roomsCount", { count: m.n });
  if (m.kind === "range") {
    const unit = m.unit ? ` ${m.unit}` : "";
    if (m.min == null) return `≤ ${fmtNum(m.max, lang)}${unit}`;
    if (m.max == null) return `> ${fmtNum(m.min, lang)}${unit}`;
    return `${fmtNum(m.min, lang)}–${fmtNum(m.max, lang)}${unit}`;
  }
  return g.label;
}

function groupLink(g) {
  if (!g.params || !Object.keys(g.params).length) return null;
  const qs = new URLSearchParams();
  Object.entries(g.params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") qs.set(k, String(v));
  });
  qs.set("src", "ai");
  return `/heatmap?${qs.toString()}`;
}

function sigColor(signal) {
  if (signal === "under_market" || signal === "strong_under_market")
    return "text-emerald-300";
  if (signal === "over_market" || signal === "strongly_over_market")
    return "text-rose-300";
  return "text-zinc-400";
}

function GroupItemRow({ it, lang }) {
  return (
    <Link
      to={`/listing/${it.id}`}
      data-testid={`ai-group-item-${it.id}`}
      className="flex items-center gap-2 rounded-lg border border-zinc-800/70 bg-zinc-950/50 px-2 py-1.5 transition hover:border-amber-500/40"
    >
      {it.image ? (
        <img src={it.image} alt="" loading="lazy"
             className="h-9 w-12 shrink-0 rounded object-cover" />
      ) : (
        <div className="h-9 w-12 shrink-0 rounded bg-zinc-800" />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[11px] text-zinc-200">{it.title}</div>
        <div className="truncate text-[10px] text-zinc-500">{it.location}</div>
      </div>
      <span className={`shrink-0 text-[11px] font-semibold ${sigColor(it.signal)}`}>
        {fmtEur(it.price, lang)}
      </span>
    </Link>
  );
}

export function ListingGroupsWidget({ widget }) {
  const { t, i18n } = useTranslation("ai");
  const lang = (i18n.language || "de").slice(0, 2);
  const groups = (widget.groups || []).filter((g) => g.count > 0);
  const [open, setOpen] = useState(() => (groups[0] ? groups[0].key : null));
  if (!groups.length) return null;
  const maxCount = Math.max(...groups.map((g) => g.count), 1);
  return (
    <div
      data-testid="ai-widget-listing-groups"
      className="space-y-1.5 rounded-xl border border-zinc-800 bg-zinc-900/70 p-3"
    >
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-zinc-500">
        <Layers className="h-3.5 w-3.5" />
        {t(`widgets.groupDims.${widget.group_by}`, { defaultValue: widget.group_by })}
        {widget.label ? ` · ${widget.label}` : ""}
      </div>
      {groups.map((g) => {
        const isOpen = open === g.key;
        const link = groupLink(g);
        const moreCount = g.count - (g.items || []).length;
        return (
          <div key={g.key} className="rounded-lg border border-zinc-800/80 bg-zinc-950/40">
            <button
              type="button"
              data-testid={`ai-group-header-${g.key}`}
              onClick={() => setOpen(isOpen ? null : g.key)}
              className="flex w-full items-center gap-2 px-2.5 py-2 text-left"
            >
              {isOpen
                ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-amber-400" />
                : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-zinc-600" />}
              <span className="min-w-0 flex-1 truncate text-sm text-zinc-100">
                {groupLabel(g, widget.group_by, t, lang)}
              </span>
              {g.deals_count > 0 && (
                <span className="shrink-0 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-300">
                  {t("widgets.groupDeals", { count: g.deals_count })}
                </span>
              )}
              <span className="shrink-0 text-[11px] text-zinc-500">
                {g.stats?.average != null && (
                  <>Ø <span className="font-semibold text-amber-400">{fmtEur(g.stats.average, lang)}</span> · </>
                )}
                {g.count}
              </span>
            </button>
            {/* proportional count bar */}
            <div className="mx-2.5 mb-1 h-0.5 rounded bg-zinc-800">
              <div
                className="h-0.5 rounded bg-amber-500/60"
                style={{ width: `${Math.max(6, (g.count / maxCount) * 100)}%` }}
              />
            </div>
            {isOpen && (
              <div className="space-y-1 px-2.5 pb-2.5" data-testid={`ai-group-items-${g.key}`}>
                {(g.items || []).map((it) => (
                  <GroupItemRow key={it.id} it={it} lang={lang} />
                ))}
                {moreCount > 0 && (
                  link ? (
                    <Link
                      to={link}
                      data-testid={`ai-group-more-${g.key}`}
                      className="flex items-center gap-1.5 px-1 pt-0.5 text-[10px] text-amber-300/90 transition hover:text-amber-200"
                    >
                      <MapPin className="h-3 w-3" />
                      {t("widgets.groupMoreLink", { count: moreCount })}
                    </Link>
                  ) : (
                    <div className="px-1 pt-0.5 text-[10px] text-zinc-600">
                      {t("widgets.groupMore", { count: moreCount })}
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        );
      })}
      {widget.unclassified > 0 && (
        <div className="px-1 text-[10px] text-zinc-600">
          {t("widgets.groupUnclassified", { count: widget.unclassified })}
        </div>
      )}
    </div>
  );
}
